import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { getToken, setToken, clearToken } from "@/lib/token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Actividad real del usuario (clic, tecla, toque, desplazamiento): se avisa al servidor una vez por minuto.
  // Mantiene viva la sesión mientras se trabaja y alimenta el «tiempo activo» del Historial de acceso.
  const ultimaInteraccion = useRef(0);
  useEffect(() => {
    if (!user) return undefined;
    const marcar = () => { ultimaInteraccion.current = Date.now(); };
    const eventos = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
    eventos.forEach((ev) => window.addEventListener(ev, marcar, { passive: true, capture: true }));
    const t = setInterval(() => {
      if (Date.now() - ultimaInteraccion.current < 60000 && document.visibilityState === "visible") {
        api.post("/auth/actividad", { activo: true }).catch(() => {});
      }
    }, 60000);
    return () => { clearInterval(t); eventos.forEach((ev) => window.removeEventListener(ev, marcar, { capture: true })); };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) {
      // Solo el Administrador General mantiene la sesión al cerrar el navegador
      setToken(data.access_token, data.persistente !== false);
    }
    // Tras login, refresca el user completo (incluye permissions)
    try {
      const { data: me } = await api.get("/auth/me");
      setUser(me);
    } catch {
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role, permissions: [] });
    }
    return data;
  };

  // Permisos efectivos (backend: /auth/me -> effective = roles configurados + legacy)
  const effective = (user && user.effective) || null;

  const hasPermission = (key) => {
    if (!user) return false;
    const perms = user.permissions || [];
    if (perms.includes("*")) return true;
    if (user.role === "admin") return true;
    if (effective) {
      if (effective.is_admin) return true;
      if ((effective.keys || []).includes(key)) return true;
      const [mod, acc] = String(key).split(".");
      if (acc && (effective.modulos?.[mod] || []).includes(acc)) return true;
    }
    return perms.includes(key);
  };

  // hasModule("ventas", "crear") -> true/false. Sin mapa efectivo (backend antiguo) no bloquea.
  const hasModule = (modulo, accion = "ver") => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!effective) return true;
    if (effective.is_admin) return true;
    return (effective.modulos?.[modulo] || []).includes(accion);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, hasModule, effective, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/**
 * Hook de permisos por módulo/acción (matriz de roles del módulo Permisos).
 * Uso en una página:
 *   const puedeCrear = usePermission("clientes", "crear");
 *   {puedeCrear && <button>Nuevo cliente</button>}
 * Acciones: "ver" | "crear" | "modificar" | "eliminar" | "imprimir".
 * Módulos: inventario, clientes, compras, contabilidad, ventas, cobranza, comisiones (GRATIVA),
 * calendario, reportes, admin, permisos, viaticos, rrhh, formularios.
 * El backend aplica las mismas reglas (403); esto solo oculta botones.
 */
export function usePermission(modulo, accion = "ver") {
  const ctx = useContext(AuthContext);
  return ctx ? ctx.hasModule(modulo, accion) : false;
}


/** Roles con fecha corriente obligatoria (p. ej. Delegado): la fecha efectiva la fija el servidor
 *  (zona horaria de la empresa) y el campo queda bloqueado. El servidor la vuelve a validar al guardar. */
export function useFechaOperacion() {
  const ctx = useContext(AuthContext);
  const user = ctx?.user;
  const fija = Boolean(user?.effective?.restricciones?.fecha_corriente);
  const hoy = user?.hoy_empresa || new Date().toLocaleDateString("en-CA");
  return { fija, hoy };
}
